/**
 * DocsSearch
 * 
 * Search input with results dropdown for documentation.
 */
import React, { useRef, useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faXmark } from '@fortawesome/free-solid-svg-icons';
import { useDocsStore } from '../../store/docsStore';
import { getCategoryConfig } from '../../docs';

export const DocsSearch: React.FC = () => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [isFocused, setIsFocused] = useState(false);

    const {
        searchQuery,
        searchResults,
        setSearchQuery,
        clearSearch,
        navigateTo
    } = useDocsStore();

    // Focus input on Cmd/Ctrl+K
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                inputRef.current?.focus();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    const handleResultClick = (docId: string) => {
        navigateTo(docId);
        clearSearch();
        inputRef.current?.blur();
    };

    const showResults = isFocused && searchQuery.length >= 2 && searchResults.length > 0;

    return (
        <div className="relative">
            {/* Search input */}
            <div className="relative">
                <FontAwesomeIcon
                    icon={faSearch}
                    className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500"
                />
                <input
                    ref={inputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setTimeout(() => setIsFocused(false), 200)}
                    placeholder="Search docs..."
                    className="w-full pl-9 pr-8 py-2 bg-gray-50 dark:bg-[#0c1520] border border-gray-200 dark:border-white/[0.06] rounded-lg
                     text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500
                     focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20"
                />
                {searchQuery && (
                    <button
                        onClick={clearSearch}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-900 dark:hover:text-white"
                    >
                        <FontAwesomeIcon icon={faXmark} className="h-3.5 w-3.5" />
                    </button>
                )}

                {/* Keyboard hint */}
                {!searchQuery && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-600 dark:text-gray-600">
                        <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-white/10 rounded text-gray-500 dark:text-gray-400">⌘K</kbd>
                    </div>
                )}
            </div>

            {/* Search results dropdown */}
            {showResults && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-[#0c1520] border border-gray-200 dark:border-white/[0.06] 
                        rounded-lg shadow-xl overflow-hidden z-10 max-h-80 overflow-y-auto">
                    {searchResults.map((result) => {
                        const categoryConfig = getCategoryConfig(result.category);

                        return (
                            <button
                                key={result.id}
                                onClick={() => handleResultClick(result.id)}
                                className="w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-white/5 transition-colors
                           border-b border-gray-100 dark:border-white/[0.06] last:border-b-0"
                            >
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-900 dark:text-white font-medium">{result.title}</span>
                                    <span className="text-xs text-gray-500">{categoryConfig?.label}</span>
                                </div>
                                {result.snippet && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">{result.snippet}</p>
                                )}
                            </button>
                        );
                    })}
                </div>
            )}

            {/* No results state */}
            {isFocused && searchQuery.length >= 2 && searchResults.length === 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 
                        rounded-lg shadow-xl p-4 text-center">
                    <p className="text-sm text-gray-500 dark:text-gray-400">No results found for "{searchQuery}"</p>
                </div>
            )}
        </div>
    );
};

export default DocsSearch;
